/**
 * TalkNote içerik şifreleme biçimi "tn1" — ORTAK DOSYA.
 *
 * Birebir aynı iki kopyası var: desktop/src/shared/contentCrypto.ts ve
 * web/lib/crypto/content-crypto.ts. Web'in Docker derlemesi yalnızca web/ klasörünü
 * görüyor, bu yüzden paylaşılamıyor; birini değiştirirsen ötekini de değiştir —
 * `npm run test:crypto` iki dosyanın aynı olduğunu ve birbirini çözdüğünü denetler.
 *
 * Yalnızca WebCrypto kullanır (globalThis.crypto.subtle): Node 20+, Electron ana
 * süreci ve tarayıcı. Ayrıntı: docs/analiz/2026-10-03-veri-koruma-programi.md Ek A.
 *
 *  - Kullanıcı anahtarı (DEK, 32 bayt) → HKDF-SHA256 ile alan ve dosya anahtarları.
 *  - Alan: `tn1.<iv>.<şifreli+etiket>` (base64url), AES-256-GCM,
 *    AAD = `tn1|<userId>|<alan>|<meetingId>` — başka kullanıcıya, alana ya da
 *    toplantıya kopyalanan değer çözülmez.
 *    Kişi ve şirket satırlarında bağlam kimliği (`meetingId` alanı) satırın kendi kimliğidir.
 *  - Arama özeti (kör indeks): e-posta, alan adı ve ad eşleştirmesi için HMAC-SHA256,
 *    kullanıcının anahtarından türetilen ayrı bir anahtarla — aynı değer aynı özeti verir,
 *    değerin kendisi özetten çıkarılamaz, başka kullanıcının özetleriyle karşılaştırılamaz.
 *  - Dosya: 16 baytlık başlık (`TN1F`, sürüm, parça boyu, 7 baytlık nonce öneki) +
 *    64 KiB'lık parçalar, her biri ayrı AES-256-GCM. Nonce = önek | parça no | son-parça
 *    bayrağı; AAD = başlık. Kesilen, sırası değişen ya da başka dosyadan gelen parça
 *    reddedilir. Dosya anahtarı toplantıya ve dosya adına bağlı.
 */

export type ContentKey = Uint8Array;

export type ContentField =
  | 'meetings.title'
  | 'meetings.summary'
  | 'meetings.key_points'
  | 'meetings.decisions'
  | 'meetings.summary_email'
  | 'meetings.participants'
  | 'transcripts.segments'
  | 'transcripts.full_text'
  | 'action_items.text'
  | 'action_items.assignee'
  | 'craft_blocks.content'
  | 'meeting_comments.body'
  | 'meetings.speaker_map'
  | 'people.name'
  | 'people.email'
  | 'people.role'
  | 'companies.name'
  | 'companies.domain'
  | 'companies.notes'
  | 'meeting_participants.name'
  | 'meeting_participants.email'
  | 'meeting_participants.role'
  | 'meeting_participants.company'
  | 'chat_threads.title'
  | 'chat_messages.body'
  | 'meeting_folders.name';

/**
 * Alanın şifrelendiği en düşük seviye (web/lib/settings/app-settings.ts > EncryptionLevel):
 * 1 içerik (Faz 2), 2 başlık ve katılımcı listeleri (Faz 3), 3 kişiler, şirketler,
 * toplantı katılımcıları ve konuşmacı eşlemesi (Faz 4). Seviyenin altındaki yazıcı alanı düz geçer.
 */
export const FIELD_LEVEL: Readonly<Record<ContentField, 1 | 2 | 3>> = {
  'meetings.title': 2,
  // Klasör adı (0059): başlıkla aynı seviye; `context` kimliği klasörün kimliği.
  'meeting_folders.name': 2,
  'meetings.summary': 1,
  'meetings.key_points': 1,
  'meetings.decisions': 1,
  'meetings.summary_email': 1,
  'meetings.participants': 2,
  'transcripts.segments': 1,
  'transcripts.full_text': 1,
  'action_items.text': 1,
  'action_items.assignee': 1,
  'craft_blocks.content': 1,
  'meeting_comments.body': 1,
  'meetings.speaker_map': 3,
  'people.name': 3,
  'people.email': 3,
  'people.role': 3,
  'companies.name': 3,
  'companies.domain': 3,
  'companies.notes': 3,
  'meeting_participants.name': 3,
  'meeting_participants.email': 3,
  'meeting_participants.role': 3,
  'meeting_participants.company': 3,
  // Sohbet geçmişi (0056): yeni veri, ayardan bağımsız her zaman şifreli yazılır (lib/chat/store.ts);
  // `context` kimliği başlıkta sohbetin, gövdede mesajın kimliği.
  'chat_threads.title': 1,
  'chat_messages.body': 1,
};

/** Arama özeti türleri — her biri ayrı ad alanı (aynı metin farklı türde farklı özet verir). */
export type IndexKind = 'email' | 'person-name' | 'company-name' | 'company-domain';

export interface FieldContext {
  userId: string;
  meetingId: string;
  field: ContentField;
}

export interface FileContext {
  meetingId: string;
  /** Depodaki adı, `.tn1` eki olmadan: `audio.mp3`, `transcript.json`, `summary.json`. */
  name: string;
}

export const FIELD_PREFIX = 'tn1.';
/** Şifreli dosyanın depo anahtarı eki: `u/<uid>/<mid>/audio.mp3.tn1`. */
export const FILE_SUFFIX = '.tn1';
export const FILE_CHUNK = 64 * 1024;

const FILE_MAGIC = [0x54, 0x4e, 0x31, 0x46]; // "TN1F"
const FILE_VERSION = 1;
const HEADER = 16;
const TAG = 16;
const KEY_BYTES = 32;
/** iv 12 bayt → 16 karakter; boş metin bile 16 baytlık etiket → en az 22 karakter. */
const SEALED_FIELD = /^tn1\.[A-Za-z0-9_-]{16}\.[A-Za-z0-9_-]{22,}$/;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Tipler globalThis.crypto'dan: masaüstü ana sürecinin derlemesinde DOM tipleri yok.
type Subtle = typeof globalThis.crypto.subtle;
type WebKey = Awaited<ReturnType<Subtle['importKey']>>;
type BufferArg = Parameters<Subtle['encrypt']>[2];

function subtle(): Subtle {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('WebCrypto kullanılamıyor');
  return s;
}

function randomBytes(length: number): Uint8Array {
  return globalThis.crypto.getRandomValues(new Uint8Array(length));
}

// Uint8Array<ArrayBufferLike> → WebCrypto'nun beklediği BufferSource (TS 5.7+ tipleri).
const buf = (bytes: Uint8Array): BufferArg => bytes as unknown as BufferArg;

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Anahtarın taşınma biçimi (API yanıtı): standart base64. */
export function encodeContentKey(key: ContentKey): string {
  let binary = '';
  for (let i = 0; i < key.length; i++) binary += String.fromCharCode(key[i]);
  return btoa(binary);
}

export function decodeContentKey(text: string): ContentKey {
  const binary = atob(text);
  if (binary.length !== KEY_BYTES) throw new Error('içerik anahtarı 32 bayt olmalı');
  const key = new Uint8Array(KEY_BYTES);
  for (let i = 0; i < KEY_BYTES; i++) key[i] = binary.charCodeAt(i);
  return key;
}

// Türetilmiş anahtarlar anahtar nesnesi başına önbellekte (aynı DEK ile binlerce alan açılıyor).
const derived = new WeakMap<ContentKey, Map<string, Promise<WebKey>>>();

function deriveKey(dek: ContentKey, info: string): Promise<WebKey> {
  if (dek.length !== KEY_BYTES) throw new Error('içerik anahtarı 32 bayt olmalı');
  let cache = derived.get(dek);
  if (!cache) derived.set(dek, (cache = new Map()));
  let key: Promise<WebKey> | undefined = cache.get(info);
  if (!key) {
    key = subtle()
      .importKey('raw', buf(dek), 'HKDF', false, ['deriveKey'])
      .then((base: WebKey) =>
        subtle().deriveKey(
          { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: buf(encoder.encode(info)) },
          base,
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt'],
        ),
      );
    cache.set(info, key);
  }
  return key;
}

function fieldAad(ctx: FieldContext): Uint8Array {
  return encoder.encode(`tn1|${ctx.userId}|${ctx.field}|${ctx.meetingId}`);
}

// ———— alanlar ————

/** Değer tn1 biçiminde şifreli bir alan mı? (İnsan yazısı bu kalıba uymaz: boşluk, nokta sayısı.) */
export function isSealedField(value: unknown): value is string {
  return typeof value === 'string' && SEALED_FIELD.test(value);
}

export async function sealField(dek: ContentKey, ctx: FieldContext, plaintext: string): Promise<string> {
  const iv = randomBytes(12);
  const key = await deriveKey(dek, 'talknote/field/v1');
  const sealed = await subtle().encrypt({ name: 'AES-GCM', iv: buf(iv), additionalData: buf(fieldAad(ctx)) }, key, buf(encoder.encode(plaintext)));
  return `${FIELD_PREFIX}${toBase64Url(iv)}.${toBase64Url(new Uint8Array(sealed))}`;
}

export async function openField(dek: ContentKey, ctx: FieldContext, sealed: string): Promise<string> {
  if (!isSealedField(sealed)) throw new Error('şifreli alan biçimi tanınmadı');
  const [, iv, data] = sealed.split('.');
  const key = await deriveKey(dek, 'talknote/field/v1');
  const plain = await subtle().decrypt({ name: 'AES-GCM', iv: buf(fromBase64Url(iv)), additionalData: buf(fieldAad(ctx)) }, key, buf(fromBase64Url(data)));
  return decoder.decode(plain);
}

/** jsonb sütunları için: değer JSON'a çevrilip şifrelenir, sütuna JSON dizgesi olarak yazılır. */
export function sealJson(dek: ContentKey, ctx: FieldContext, value: unknown): Promise<string> {
  return sealField(dek, ctx, JSON.stringify(value));
}

/** Şifreliyse çözüp JSON'u açar; değilse değeri olduğu gibi döndürür (eski, düz kayıtlar). */
export async function openJson<T>(dek: ContentKey, ctx: FieldContext, stored: unknown): Promise<T> {
  return isSealedField(stored) ? (JSON.parse(await openField(dek, ctx, stored)) as T) : (stored as T);
}

// ———— arama özeti (kör indeks) ————

/**
 * Eşleştirme kuralları burada: e-posta ve alan adı küçük harfe, şirket adı küçük harfe
 * (büyük/küçük harf duyarsız eşleşir), kişi adı yalnızca boşlukları düzeltilerek
 * (harf duyarlı — desktop/src/services/directoryRules.ts > normalizePersonName).
 */
export function indexValue(kind: IndexKind, value: string): string {
  const text = value.normalize('NFC').replace(/\s+/g, ' ').trim();
  return kind === 'person-name' ? text : text.toLowerCase();
}

const indexKeys = new WeakMap<ContentKey, Promise<WebKey>>();

function indexKey(dek: ContentKey): Promise<WebKey> {
  if (dek.length !== KEY_BYTES) throw new Error('içerik anahtarı 32 bayt olmalı');
  let key = indexKeys.get(dek);
  if (!key) {
    key = subtle()
      .importKey('raw', buf(dek), 'HKDF', false, ['deriveBits'])
      .then((base: WebKey) =>
        subtle().deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: buf(encoder.encode('talknote/index/v1')) }, base, 256),
      )
      .then((bits: ArrayBuffer) => subtle().importKey('raw', bits, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']));
    indexKeys.set(dek, key);
  }
  return key;
}

/** Değerin arama özeti: base64url, 43 karakter. Boş değerin özeti olmaz (boş dizgi döner). */
export async function blindIndex(dek: ContentKey, kind: IndexKind, value: string): Promise<string> {
  const normalized = indexValue(kind, value);
  if (!normalized) return '';
  const mac = await subtle().sign('HMAC', await indexKey(dek), buf(encoder.encode(`${kind}|${normalized}`)));
  return toBase64Url(new Uint8Array(mac));
}

// ———— dosyalar ————

function fileNonce(prefix: Uint8Array, index: number, last: boolean): Uint8Array {
  const nonce = new Uint8Array(12);
  nonce.set(prefix, 0);
  new DataView(nonce.buffer).setUint32(7, index);
  nonce[11] = last ? 1 : 0;
  return nonce;
}

function fileKeyInfo(ctx: FileContext): string {
  return `talknote/file/v1|${ctx.meetingId}|${ctx.name}`;
}

/** İçerik tn1 dosya başlığıyla mı başlıyor? */
export function isSealedFile(bytes: Uint8Array): boolean {
  return bytes.length >= HEADER && FILE_MAGIC.every((b, i) => bytes[i] === b) && bytes[4] === FILE_VERSION;
}

export async function sealFile(dek: ContentKey, ctx: FileContext, plain: Uint8Array): Promise<Uint8Array> {
  const key = await deriveKey(dek, fileKeyInfo(ctx));
  const header = new Uint8Array(HEADER);
  header.set(FILE_MAGIC, 0);
  header[4] = FILE_VERSION;
  new DataView(header.buffer).setUint32(5, FILE_CHUNK);
  const prefix = randomBytes(7);
  header.set(prefix, 9);

  const chunks = Math.max(1, Math.ceil(plain.length / FILE_CHUNK));
  const out = new Uint8Array(HEADER + plain.length + chunks * TAG);
  out.set(header, 0);
  let offset = HEADER;
  for (let i = 0; i < chunks; i++) {
    const part = plain.subarray(i * FILE_CHUNK, Math.min(plain.length, (i + 1) * FILE_CHUNK));
    const iv = fileNonce(prefix, i, i === chunks - 1);
    const sealed = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: buf(iv), additionalData: buf(header) }, key, buf(part)));
    out.set(sealed, offset);
    offset += sealed.length;
  }
  return out;
}

export async function openFile(dek: ContentKey, ctx: FileContext, sealed: Uint8Array): Promise<Uint8Array> {
  if (!isSealedFile(sealed)) throw new Error('şifreli dosya başlığı tanınmadı');
  const header = sealed.subarray(0, HEADER);
  const chunkSize = new DataView(header.buffer, header.byteOffset, HEADER).getUint32(5);
  if (chunkSize < 1 || chunkSize > 16 * 1024 * 1024) throw new Error('şifreli dosyada geçersiz parça boyu');
  const prefix = header.subarray(9, 16);
  const key = await deriveKey(dek, fileKeyInfo(ctx));

  const parts: Uint8Array[] = [];
  let total = 0;
  let pos = HEADER;
  let index = 0;
  do {
    const length = Math.min(chunkSize + TAG, sealed.length - pos);
    if (length < TAG) throw new Error('şifreli dosya kesik');
    const last = pos + length === sealed.length;
    const plain = new Uint8Array(
      await subtle().decrypt(
        { name: 'AES-GCM', iv: buf(fileNonce(prefix, index, last)), additionalData: buf(new Uint8Array(header)) },
        key,
        buf(sealed.subarray(pos, pos + length)),
      ),
    );
    parts.push(plain);
    total += plain.length;
    pos += length;
    index++;
  } while (pos < sealed.length);

  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** `u/<userId>/<meetingId>/<ad>.tn1` → dosya bağlamı; şifreli dosya anahtarı değilse null. */
export function fileContextFromKey(storageKey: string): FileContext | null {
  const match = /^u\/[0-9a-f-]{36}\/([0-9a-f-]{36})\/([a-z0-9._-]+)\.tn1$/i.exec(storageKey);
  return match ? { meetingId: match[1], name: match[2] } : null;
}
